import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import FormComponent from 'components/UI/Input/FormComponent';
import Container from 'components/UI/Layout/Container';
import Col from 'components/UI/Layout/Col';
import EditableTextInput from 'components/UI/Input/EditableTextInput/index';
import EditableSelect from 'components/UI/Input/EditableSelect';
import StateInput from 'components/UI/Input/StateInput';
import Row from 'components/UI/Layout/Row';
import produce from 'immer';
import { common as travelAgencyRules, travelAgencyData } from 'services/validation/rules/travelAgency.rules';
import TravelAgenciesRepository from 'services/repositories/TravelAgenciesRepository';
import { isEmpty } from 'lodash';
import Form from 'services/validation/Form';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import LoadingSpinner from 'components/UI/LoadingIndicators/LoadingSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ErrorMessage from 'components/UI/ErrorMessage';
import { LOCATIONS } from 'constants/locations';

function SignUpTravelAgencyForm({ nextStep, formData })
{
    const [form, setForm] = useState(new Form(formData.travelAgency, travelAgencyRules));
    const [fetchedTravelAgency, setFetchedTravelAgency] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const identifierTypes = ['IATA', 'ARC', 'CLIA', 'TRUE'].map((prefix) => ({ label: prefix, value: prefix }));
    const affiliations = [
        'Virtuoso',
        'TripArc',
        'Travel Leaders Select',
        'Signature',
        'Serandipians',
        'AMEX',
        'TDC',
        'Voyages en Direct',
        'Ensemble',
        'Independent',
    ].map((prefix) => ({ label: prefix, value: prefix }));

    const [isLoading, setIsLoading] = useState(false);

    function sanitizeString(str)
    {
        return String(str ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().trim();
    }

    const identifierChanged = (value, path) =>
    {
        const _form = { ...form };
        const updatedForm = produce(_form, (draft) =>
        {
            draft.data[path] = sanitizeString(value);
        });
        handleFormChange(updatedForm, path);
    };

    const countryChanged = (value, path) =>
    {
        const _form = { ...form };
        const updatedForm = produce(_form, (draft) =>
        {
            draft.data[path] = value?.label ?? '';
            draft.data.state = '';
        });
        handleFormChange(updatedForm, path);
    };

    const handleConsortiaChange = (value) =>
    {
        const updatedForm = produce(form, (draft) =>
        {
            draft.data = { ...form.data, consortia_affiliation: value?.value };
        });

        setForm(updatedForm);
    };

    const handleStateChange = (value, path) =>
    {
        const _form = { ...form };
        const updatedForm = produce(_form, (draft) =>
        {
            draft.data[path] = value;
        });
        handleFormChange(updatedForm, path);
    };

    const handleIdentifierTypeChange = (value) =>
    {
        const updatedForm = produce(form, (draft) =>
        {
            draft.data.identifier_type = value?.value;
        });

        setForm(updatedForm);
    };
    const handleFormChange = (_form) =>
    {
        setForm(_form);
    };

    const fetchTravelAgency = async () =>
    {
        const data = {
            identifier_type: form.data.identifier_type,
            identifier_number: form.data.identifier_number,
        };

        setIsLoading(true);
        const response = await (new TravelAgenciesRepository()).getByIdentifier(data);

        setIsLoading(false);

        if (!response.isSuccess())
        {
            return false;
        }

        setFetchedTravelAgency(true);
        const responseData = response.getData();
        // Travel Agency doesnt exist, add travel agency data rules
        if (isEmpty(responseData))
        {
            const formValidations = {
                ...travelAgencyRules,
                ...travelAgencyData,
            };
            setForm(new Form(form.data, formValidations));
            return true;
        }

        const _form = produce(form, (draft) =>
        {
            draft.data.id = responseData.id;
            draft.data.name = responseData.name;
            draft.data.consortia_affiliation = responseData.consortia_affiliation;
            draft.data.address = responseData.address;
            draft.data.city = responseData.city;
            draft.data.state = responseData.state;
            draft.data.country = responseData.country;
            draft.data.zip = responseData.zip;
        });

        setForm(_form);
        nextStep({ ...formData, travelAgency: _form.data });
        return true;
    };

    const continueStep = () =>
    {
        const _form = produce(form, (draft) =>
        {
            draft.validate();
        });

        setForm({ ..._form });
        if (!_form.isValid())
        {
            return;
        }

        setIsLoading(true);

        if (!fetchedTravelAgency && _form.isValid())
        {
            fetchTravelAgency()
                .then((r) =>
                {
                    if (!r)
                    {
                        setErrorMessage('Could not connect with the service. Try Again!');
                    }
                })
                .finally(() =>
                {
                    setIsLoading(false);
                });
        }

        if (fetchedTravelAgency && _form.isValid())
        {
            nextStep({ ...formData, travelAgency: _form.data });
            setIsLoading(false);
        }
    };

    return (
        <FormComponent source={form} onFormChange={handleFormChange}>
            <Container className="tw-px-4 tw-mb-4 tw-rounded-md">
                <Row className="tw-mb-3 tw-justify-center">
                    <span className="tw-font-regular">Please provide travel agency information</span>
                </Row>
                <ErrorMessage errorMessage={errorMessage} />

                <Row className="tw-mb-3 tw-w-full">
                    <Col className="tw-w-full">
                        <EditableSelect
                            editing={!fetchedTravelAgency && !isLoading}
                            containerClassName="tw-flex-wrap tw-mb-3"
                            labelContainerClassName="tw-flex-auto tw-w-full"
                            inputProps={{
                                getOptionLabel: (option) => option?.label ?? form.data.identifier_type,
                                getOptionValue: (option) => option,
                                options: identifierTypes,
                                error: form.errorsBag.errors.identifier_type,
                                onChange: (value) => handleIdentifierTypeChange(value),
                            }}
                            labelText="Identifier Type"
                        />
                        <EditableTextInput
                            editing={!fetchedTravelAgency && !isLoading}
                            labelText="Identifier No."
                            text={form.data.identifier_number}
                            inputProps={{
                                path: 'identifier_number',
                                error: form.errorsBag.errors.identifier_number,
                                onChange: identifierChanged,
                            }}
                        />
                    </Col>
                </Row>

                { (fetchedTravelAgency && !form.data.id)
                    && (
                        <>
                            <Row className="tw-mb-3">
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="Agency Name"
                                        text={form.data.name}
                                        inputProps={{
                                            path: 'name',
                                            error: form.errorsBag.errors.name,
                                        }}
                                    />
                                </Col>
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableSelect
                                        editing={!isLoading}
                                        containerClassName="tw-flex-wrap tw-mb-3"
                                        labelContainerClassName="tw-flex-auto tw-w-full"
                                        inputProps={{
                                            getOptionLabel: (option) => option?.label ?? form.data.consortia_affiliation,
                                            getOptionValue: (option) => option.value,
                                            options: affiliations,
                                            error: form.errorsBag.errors.consortia_affiliation,
                                            onChange: (value) => handleConsortiaChange(value),
                                        }}
                                        labelText="Consortia Affiliation"
                                    />
                                </Col>
                            </Row>

                            <Row className="tw-mb-3">
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableSelect
                                        editing={!isLoading}
                                        containerClassName="tw-flex-wrap tw-mb-3"
                                        labelContainerClassName="tw-flex-auto tw-w-full"
                                        inputProps={{
                                            getOptionLabel: (option) => option?.label ?? form.data.country,
                                            getOptionValue: (option) => option.label,
                                            options: LOCATIONS.COUNTRIES,
                                            error: form.errorsBag.errors.country,
                                            onChange: (value) => countryChanged(value, 'country'),
                                        }}
                                        labelText="Country"
                                    />
                                </Col>
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <StateInput
                                        key={form.data.country}
                                        country={form.data.country}
                                        isLoading={isLoading}
                                        value={form.data.state}
                                        onChange={handleStateChange}
                                        error={form.errorsBag.errors.state}
                                        labelText="State"
                                    />
                                </Col>
                            </Row>

                            <Row className="tw-mb-3">
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="City"
                                        text={form.data.city}
                                        inputProps={{
                                            path: 'city',
                                            error: form.errorsBag.errors.city,
                                        }}
                                    />
                                </Col>
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="Address"
                                        text={form.data.address}
                                        inputProps={{
                                            path: 'address',
                                            error: form.errorsBag.errors.address,
                                        }}
                                    />
                                </Col>
                            </Row>

                            <Row className="tw-mb-3">
                                <Col md={6} sm={6} xs={12} spacing={2}>
                                    <EditableTextInput
                                        editing={!isLoading}
                                        labelText="Zip"
                                        text={form.data.zip}
                                        inputProps={{
                                            path: 'zip',
                                            error: form.errorsBag.errors.zip,
                                        }}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}

                <Row className="tw-justify-center tw-mt-5">
                    <Button
                        className="tw-py-2"
                        onClick={() => continueStep()}
                        disabled={isLoading}
                    >
                        Next {!isLoading && <FontAwesomeIcon className="tw-ml-4" icon={faChevronRight} />} <LoadingSpinner className="tw-ml-4" loading={isLoading} />
                    </Button>
                </Row>
            </Container>
        </FormComponent>
    );
}

SignUpTravelAgencyForm.defaultProps = {

};

SignUpTravelAgencyForm.propTypes = {
    nextStep: PropTypes.func,
    formData: PropTypes.any,
};

export default SignUpTravelAgencyForm;
